import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronDown, Star, ArrowRight } from "lucide-react";
import client from "@/lib/api";
import IntroVideo from "@/components/IntroVideo";
import GoalCard from "@/components/GoalCard";

export default function Roadmap() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [openKey, setOpenKey] = useState(null);

  useEffect(() => {
    client.get("/roadmap").then(({ data }) => {
      setData(data);
      if (data.completed_diagnostic) {
        const nextP = data.phases.find((p) => p.tasks.some((t) => !t.done)) || data.phases[0];
        setOpenKey(nextP.key);
      }
    });
  }, []);

  if (!data) return <div className="grid h-64 place-items-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-neutral-300 border-t-[var(--tenant-primary)]" /></div>;

  if (!data.completed_diagnostic) {
    return (
      <div className="mx-auto max-w-lg rounded-2xl border border-[#E5E2DC] bg-white p-10 text-center">
        <h1 className="font-head text-2xl font-semibold text-neutral-900">Let's build your roadmap</h1>
        <p className="mt-2 text-neutral-600">Take the quick diagnostic first and we'll tailor your steps.</p>
        <button data-testid="roadmap-start-diagnostic" onClick={() => navigate("/diagnostic")} className="mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold text-white" style={{ backgroundColor: "var(--tenant-primary)" }}>Start diagnostic <ArrowRight size={16} /></button>
      </div>
    );
  }

  const toggle = async (phaseKey, taskIndex) => {
    const { data: res } = await client.post("/roadmap/toggle", { phase_key: phaseKey, task_index: taskIndex });
    setData({ ...data, phases: res.phases, progress: res.progress });
  };

  const { phases, progress } = data;

  return (
    <div className="fade-up space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-neutral-400">Your action roadmap</p>
          <h1 className="mt-2 font-head text-3xl font-semibold tracking-tight text-neutral-900">One step at a time</h1>
          <p className="mt-2 max-w-xl text-neutral-600">Check off a step when you finish it. There's no deadline — momentum matters more than speed.</p>
        </div>
        <div className="rounded-2xl border border-[#E5E2DC] bg-white px-6 py-4 text-center" data-testid="roadmap-progress">
          <p className="font-head text-3xl font-semibold text-neutral-900">{progress.percent}%</p>
          <p className="text-sm text-neutral-500">{progress.done}/{progress.total} steps</p>
        </div>
      </div>

      <GoalCard />

      <IntroVideo
        testid="intro-video-roadmap"
        title="28 steps? Nope. Just one this week."
        subtitle="Watch Penny & Ollie (50 sec), then start your first step right below."
        embedUrl="https://dubb.com/v/yi4TY2/embed?width=auto&height=auto&autoplay=0&no_cta=1&no_controls=0&muted=0"
        paddingBottom="56.10%"
      />

      <div className="space-y-4">
        {phases.map((p, idx) => {
          const done = p.tasks.filter((t) => t.done).length;
          const complete = done === p.tasks.length;
          const isOpen = openKey === p.key;
          return (
            <div key={p.key} className="overflow-hidden rounded-2xl border border-[#E5E2DC] bg-white" data-testid={`phase-${p.key}`}>
              <button onClick={() => setOpenKey(isOpen ? null : p.key)} data-testid={`phase-toggle-${p.key}`} className="flex w-full items-center gap-4 px-6 py-5 text-left">
                <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl font-head font-semibold ${complete ? "tenant-bg text-white" : "tenant-light-bg tenant-text"}`}>
                  {complete ? <Check size={18} /> : idx + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-head text-lg font-medium text-neutral-900">{p.title}</h3>
                    {p.recommended && <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold tenant-text" style={{ backgroundColor: "var(--tenant-primary-light)" }}><Star size={11} /> Recommended start</span>}
                    <span className="text-xs text-neutral-400">{p.chapter}</span>
                  </div>
                  <p className="mt-0.5 text-sm text-neutral-500">{p.subtitle}</p>
                </div>
                <span className="hidden text-sm text-neutral-400 sm:block">{done}/{p.tasks.length}</span>
                <ChevronDown size={20} className={`shrink-0 text-neutral-400 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div className="border-t border-[#E5E2DC] px-6 py-5">
                  <ul className="space-y-2.5">
                    {p.tasks.map((t, i) => (
                      <li key={i}>
                        <button data-testid={`task-${p.key}-${i}`} onClick={() => toggle(p.key, i)} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-neutral-50">
                          <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-md border transition-all ${t.done ? "tenant-bg border-transparent" : "border-neutral-300"}`}>
                            {t.done && <Check size={14} className="text-white" />}
                          </span>
                          <span className={`${t.done ? "text-neutral-400 line-through" : "text-neutral-700"}`}>{t.label}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
